import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../../config/types';
import Details from '../Details';
import ErrorMessage from '../ErrorMessage';
import GradeInput from './GradeInput';
import { dateIsEmpty } from './utils';

interface GradeInputProps {
  name: string;
  defaultValue?: string; // An ISO date string with only the yyyy-mm-dd
  disabled?: boolean;
  dayLabel?: string;
  monthLabel?: string;
  yearLabel?: string;
  anchorId?: string;
  onChange?(value: string): void;
}

export function GradeInputWrapper({
  name,
  defaultValue,
  disabled = false,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const dayId = useMemo(() => `${anchorId}`, [anchorId]);
  const monthId = useMemo(() => `${anchorId}2`, [anchorId]);
  const yearId = useMemo(() => `${anchorId}3`, [anchorId]);
  const g7Id = useMemo(() => `${anchorId}4`, [anchorId]);

  const values = useMemo<string[]>(() => {
    const initValue = defaultValue ?? '--';
    return initValue.split('-');
  }, [defaultValue]);
  const [g7Value, setG7Value] = useState(values[0]);
  const [dayValue, setDayValue] = useState(values[3]);
  const [monthValue, setMonthValue] = useState(values[2]);
  const [yearValue, setYearValue] = useState(values[1]);

  const triggerChange = (newValue: string) => {
    if (onChange) {
      if (dateIsEmpty(newValue)) {
        onChange('');
      } else {
        onChange(newValue);
      }
    }
  };

  const handleDayChange = (day: string) => {
    setDayValue(day);
    triggerChange(`${g7Value}-${yearValue}-${monthValue}-${day}`);
  };

  const handleMonthChange = (month: string) => {
    setMonthValue(month);
    triggerChange(`${g7Value}-${yearValue}-${month}-${dayValue}`);
  };

  const handleYearChange = (year: string) => {
    setYearValue(year);
    triggerChange(`${g7Value}-${year}-${monthValue}-${dayValue}`);
  };
  const handleG7Change = (value: string) => {
    setG7Value(value);
    triggerChange(`${value}-${yearValue}-${monthValue}-${dayValue}`);
  };

  return (
    <div className="govuk-date-input">
      <GradeInput
        testId={`${name}-day`}
        disabled={disabled}
        label="SCS2"
        anchorId={dayId}
        onChange={handleDayChange}
        value={dayValue}
      />
      <GradeInput
        testId={`${name}-month`}
        disabled={disabled}
        label="SCS1"
        anchorId={monthId}
        onChange={handleMonthChange}
        value={monthValue}
      />
      <GradeInput
        testId={`${name}-year`}
        disabled={disabled}
        label="Grade 6"
        anchorId={yearId}
        onChange={handleYearChange}
        value={yearValue}
      />
      <GradeInput
        testId={`${name}-g7`}
        disabled={disabled}
        label="Grade7"
        anchorId={g7Id}
        onChange={handleG7Change}
        value={g7Value}
      />
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function GradeInputField({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    // If the value is currently not defined in the form, initialise it
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { onChange, name } = register(question.name);

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-date-input">
        <GradeInputWrapper
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
